import { useState } from "react";
import { SbNotification } from "uiCore";
import useRedeploymentPlanStore, { useHasRedeploymentPlanDraft } from "Store/useRedeploymentPlanStore";
import { addTalentMobilityRedeploymentPlan, updateTalentMobilityRedeploymentPlan } from "Api/apiService";

// Shared Save Plan / Save & Close Plan flow for the redeployment screens
// (Redeployment Center and Reduction Detail) — both build one plan draft in
// useRedeploymentPlanStore and both render the shared PlanSaveButton
// (components/PlanSaveButton.jsx), which calls savePlan() below.
//
// The store remembers which plan the draft is attached to (planId/planName,
// set by setPlan once a save succeeds), so:
//   - the FIRST save on a fresh draft has no planId yet -> savePlan POSTs a
//     new plan, and the response's plan_id/plan_name land back in the store;
//   - every save after that has a planId -> savePlan PUTs an update to that
//     same plan (replacing its detail rows with the current draft) instead
//     of creating a duplicate.
//
// "Save Plan" keeps the draft (and the in-progress plan) open for more
// edits; "Save & Close Plan" runs the same save, then also clears the draft
// (reset()) and fires onClosed — e.g. to navigate away, mirroring what the
// old single Create Plan flow always did.
const useRedeploymentPlanActions = ({ onClosed } = {}) => {
  const planId = useRedeploymentPlanStore((state) => state.planId);
  const planName = useRedeploymentPlanStore((state) => state.planName);
  const getRedeploymentPlanPayload = useRedeploymentPlanStore((state) => state.getRedeploymentPlanPayload);
  const setPlan = useRedeploymentPlanStore((state) => state.setPlan);

  // Save buttons disable themselves while the draft is empty; the guard in
  // savePlan stays as a backstop.
  const hasDraft = useHasRedeploymentPlanDraft();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isUpdate = Boolean(planId);
  const planLabel = isUpdate ? planName : "New Plan";

  // Returns true on success so the calling popover knows it can close itself
  // (left open on failure, so the typed name isn't lost on a retry).
  const savePlan = async (rawPlanName, { closeAfter = false } = {}) => {
    if (!hasDraft) {
      SbNotification.info("Nothing to save", "No redeployment plan data has been drafted yet.");
      return false;
    }
    const trimmedPlanName = (rawPlanName || "").trim();
    if (!trimmedPlanName) {
      SbNotification.info("Plan name required", "Please enter a plan name.");
      return false;
    }

    const payload = getRedeploymentPlanPayload(trimmedPlanName);
    setIsSubmitting(true);
    try {
      const response = isUpdate
        ? await updateTalentMobilityRedeploymentPlan(planId, payload)
        : await addTalentMobilityRedeploymentPlan(payload);
      const savedPlanName = response?.plan_name || trimmedPlanName;

      if (closeAfter) {
        // The draft is now the backend's — clear it (and the flow's session
        // shortlist tags) so the next draft starts from scratch. reset()
        // also clears planId/planName, so the header goes back to "New Plan".
        useRedeploymentPlanStore.getState().reset();
      } else {
        setPlan(response?.plan_id ?? planId, savedPlanName);
      }

      SbNotification.success(
        isUpdate ? "Redeployment plan updated" : "Redeployment plan saved",
        `"${savedPlanName}" plan ${isUpdate ? "updated" : "saved"}.`
      );
      if (closeAfter) onClosed?.();
      return true;
    } catch (error) {
      SbNotification.error(
        isUpdate ? "Unable to update redeployment plan" : "Unable to save redeployment plan",
        error?.response?.data?.message || "Please try again."
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { hasDraft, isSubmitting, planId, planName, planLabel, isUpdate, savePlan };
};

export default useRedeploymentPlanActions;
